import React from 'react';

import { groupSelectionKey } from './types';

const PREVIEW_DEBOUNCE_MS = 300;

interface UseGroupLicencePreviewOptions {
  enabled?: boolean;
  scopeKey?: string;
}

function useGroupLicencePreview<T>(
  groupUuids: string[],
  fetchPreview: () => Promise<T>,
  { enabled = true, scopeKey = '' }: UseGroupLicencePreviewOptions = {}
): T | null {
  const groupKey = groupSelectionKey(groupUuids);
  const [preview, setPreview] = React.useState<T | null>(null);

  const fetchRef = React.useRef(fetchPreview);
  React.useEffect(() => {
    fetchRef.current = fetchPreview;
  });

  React.useEffect(() => {
    // Cleared on every change, not just the empty one: stale counts read as the new group's.
    setPreview(null);
    if (!groupKey || !enabled) return undefined;

    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const data = await fetchRef.current();
        if (!cancelled) setPreview(data);
      } catch {
        if (!cancelled) setPreview(null);
      }
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [groupKey, enabled, scopeKey]);

  return preview;
}

export default useGroupLicencePreview;
